import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, AlertCircle, CheckCircle, Clock, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiClient } from '../utils/apiClient';
import { formatDate } from '../utils/formatters';

interface TestStatus {
  id: string;
  status: string;
  totalDocuments: number;
  processedDocuments: number;
  successfulDocuments: number;
  failedDocuments: number;
  overallAccuracy: number;
  progress: number;
  startedAt: string;
  completedAt: string | null;
}

export const TestRunner: React.FC = () => {
  const navigate = useNavigate();
  const [currentTest, setCurrentTest] = useState<TestStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const [goldenDocuments, setGoldenDocuments] = useState(0);

  useEffect(() => {
    fetchGoldenDocumentsCount();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (currentTest && currentTest.status === 'running') {
      interval = setInterval(() => {
        fetchTestStatus(currentTest.id);
      }, 2000); // Poll every 2 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentTest]);

  const fetchGoldenDocumentsCount = async () => {
    try {
      const response = await apiClient.get('/api/documents?status=golden&limit=1');
      setGoldenDocuments(response.data.pagination.total);
    } catch (error) {
      console.error('Failed to fetch golden documents count:', error);
    }
  };

  const fetchTestStatus = async (testId: string) => {
    try {
      const response = await apiClient.get(`/api/tests/${testId}/status`);
      setCurrentTest(response.data.test);
      
      if (response.data.test.status === 'completed') {
        toast.success('Test completed successfully!');
      } else if (response.data.test.status === 'failed') {
        toast.error('Test failed');
      }
    } catch (error) {
      console.error('Failed to fetch test status:', error);
    }
  };

  const startTest = async () => {
    if (goldenDocuments === 0) {
      toast.error('No golden documents available. Please validate some documents first.');
      return;
    }

    setStarting(true);
    try {
      const response = await apiClient.post('/api/tests/start');
      
      if (response.data.success) {
        setCurrentTest(response.data.test);
        toast.success('Test started successfully');
      }
    } catch (error: any) {
      console.error('Failed to start test:', error);
      toast.error('Failed to start test: ' + (error.response?.data?.message || error.message));
    } finally {
      setStarting(false);
    }
  };

  const viewResults = () => {
    if (currentTest) {
      navigate(`/tests/${currentTest.id}`);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'running':
        return <Clock className="h-8 w-8 text-yellow-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-8 w-8 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-8 w-8 text-red-500" />;
      default:
        return <Play className="h-8 w-8 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'running':
        return 'Test in Progress';
      case 'completed':
        return 'Test Completed';
      case 'failed':
        return 'Test Failed';
      default:
        return 'Ready to Start';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Run Test</h1>
        <p className="text-gray-600 mt-2">
          Test API accuracy against your golden standard documents
        </p>
      </div>

      {/* Prerequisites Check */}
      <div className="card mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Prerequisites</h3>
        
        <div className="space-y-3">
          <div className={`flex items-center space-x-3 ${
            goldenDocuments > 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            {goldenDocuments > 0 ? (
              <CheckCircle className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span>
              {goldenDocuments} golden document{goldenDocuments !== 1 ? 's' : ''} available
              {goldenDocuments === 0 && ' (minimum 1 required)'}
            </span>
          </div>
          
          {goldenDocuments === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-sm text-yellow-800">
                You need to validate at least one document before running tests.{' '}
                <a href="/documents?status=pending" className="font-medium underline">
                  Go to validation →
                </a>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Test Status */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            {getStatusIcon(currentTest?.status || 'idle')}
            <div>
              <h3 className="text-xl font-medium text-gray-900">
                {getStatusText(currentTest?.status || 'idle')}
              </h3>
              {currentTest && (
                <p className="text-sm text-gray-500">
                  Started: {formatDate(currentTest.startedAt, true)}
                </p>
              )}
            </div>
          </div>

          {!currentTest ? (
            <button
              onClick={startTest}
              disabled={starting || goldenDocuments === 0}
              className="btn-primary"
            >
              {starting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Starting...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Start Test
                </>
              )}
            </button>
          ) : currentTest.status === 'completed' ? (
            <button onClick={viewResults} className="btn-primary">
              <TrendingUp className="h-4 w-4 mr-2" />
              View Results
            </button>
          ) : null}
        </div>

        {/* Progress */}
        {currentTest && (
          <div className="space-y-6">
            {/* Progress Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm text-gray-500">
                  {currentTest.processedDocuments} / {currentTest.totalDocuments}
                </span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${currentTest.progress || 0}%` }}
                ></div>
              </div>
              <div className="text-center mt-2">
                <span className="text-lg font-medium text-gray-900">
                  {(currentTest.progress || 0).toFixed(1)}%
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {currentTest.totalDocuments}
                </div>
                <div className="text-sm text-blue-800">Total Documents</div>
              </div>
              
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {currentTest.successfulDocuments}
                </div>
                <div className="text-sm text-green-800">Successful</div>
              </div>
              
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">
                  {currentTest.failedDocuments}
                </div>
                <div className="text-sm text-red-800">Failed</div>
              </div>
            </div>

            {/* Accuracy */}
            {currentTest.status === 'completed' && (
              <div className="text-center p-6 bg-gray-50 rounded-lg">
                <div className="text-4xl font-bold text-gray-900 mb-2">
                  {(currentTest.overallAccuracy || 0).toFixed(1)}%
                </div>
                <div className="text-lg text-gray-600">Overall Accuracy</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Instructions */}
      {!currentTest && (
        <div className="card border-blue-200 bg-blue-50">
          <h3 className="text-lg font-medium text-blue-900 mb-3">How Testing Works</h3>
          <div className="text-sm text-blue-800 space-y-2">
            <p>1. All documents with golden data are processed through the API</p>
            <p>2. Results are compared field-by-field with your validated data</p>
            <p>3. Any difference counts as an error for that document</p>
            <p>4. Overall accuracy = percentage of documents with zero errors</p>
          </div>
        </div>
      )}
    </div>
  );
};